/**
 * https://tapd.woa.com/10145891/prong/stories/view/1010145891883406775
 */

import { checkUserAct } from '@/api';
import { TaskType, taskTypeList } from '@/config/task';
import { ExecConf, TaskGroupConfig, TaskItemConfig } from '@/types/config-type';
import { ExecTaskType, JumpInfo, TaskItem, TaskStatus } from '@/types/task';
import { fillNum } from '@/utils';
import { getMonthPeriod, getServerTime } from '@/utils/date-helper';
import { CustomException, elk } from '@tencent/ppd-base-libs';
import { CheckUserActRes, execUseract, TaskInfo, taskShow, TaskShowRes } from '@tencent/wphk-act-base-cgi';
import { ExecActState } from '@tencent/wphk-act-base-cgi/dist/activity/exec-useract/types';
import dayjs from 'dayjs';
import { flatten } from 'lodash-es';
import { defineStore } from 'pinia';
import { useConfigStore } from './config';

/** 任务汇总信息 */
type TaskInfoItem = {
  /** 任务ID */
  taskNameId: string;
  /** 最大完成次数(每周期) */
  maxTimesPerStage: number;
  /** 已完成次数 */
  finishCount: number;
  /** 任务已派印花数量 */
  taskSendCnt: number;
  /** 任务派印花总限额 */
  taskTotalCnt: number;
  /** 【本周期】单次任务信息 - 完成一次任务记录一条信息（如：完成所需资格数，已获得资格数，完成时间，等等） */
  taskInfoDetail: TaskInfo[]
};
//
export type TaskStore = {
  taskInfoList: TaskInfoItem[]
};

type TaskStoreInfo = TaskStore['taskInfoList'];

export type TaskGroupInfo = {
  taskGroupType: TaskType;
  taskInfoList: TaskStoreInfo;
};

// type TaskSummaryItems = {
//   infoItem: TaskInfoItem[];
//   groupType: string;
// };

export const useTaskStore = defineStore('task', {
  state: () => ({
    taskInfoList: [] as TaskStoreInfo,
  }),
  getters: {
    /** 完成所有任务可获得奖品数目 */
    allTaskPrizeCount(state): number {
      return state.taskInfoList
        .map(item => this.getTaskInfo(item.taskNameId))
        .reduce((acc, cur) => {
          const { maxTimesPerStage, prizeCountPerTime } = cur;
          // 最大完成次数 * 每次获得奖品个数
          return acc + maxTimesPerStage * prizeCountPerTime;
        }, 0);
    },
    /** 任务组信息列表 */
    taskGroupList(state) {
      const { taskItemConfig = [] } = useConfigStore().config.taskConfig;
      return taskTypeList.map(taskGroupType => {
        const taskNameIdList = taskItemConfig
          .filter(item => item.taskType === taskGroupType)
          .map(item => item.taskNameId);
        const taskInfoList = state.taskInfoList.filter(item => taskNameIdList.includes(item.taskNameId));
        return {
          taskGroupType,
          taskInfoList,
          isDoneAllTask: taskInfoList.every(item => item.finishCount >= item.maxTimesPerStage),
        };
      });
    },
    /** 可展示的任务组列表(还有未完成任务项的组(组里可以包含已完成的任务项)) */
    showTaskGroupList(): TaskGroupInfo[] {
      const list = this.taskGroupList.filter(({ taskInfoList }) => {
        // 有任务
        const hasTaskList = taskInfoList.length > 0;
        // 有可以做的任务
        const hasCanDoTask = taskInfoList
          .map(item => this.getTaskInfo(item.taskNameId))
          .some(taskItem => isTaskCanDo(taskItem));
        return hasTaskList && hasCanDoTask;
      });
      return list;
    },
    /** 是否完成了所有可以完成的任务 */
    isCannoDoMoreTask(): boolean {
      return this.showTaskGroupList.length === 0;
    },
    /** 主动任务(目前最多一个) */
    autoExecTaskInfo(state) {
      const { taskItemConfig = [] } = useConfigStore().config.taskConfig;
      const taskConfig = taskItemConfig.find(taskConfig => {
        const { autoExecConfig, taskNameId } = taskConfig;
        // 有主动任务配置
        const hasExecConfig = !!autoExecConfig;
        // 在用户的任务列表中
        const isInUserAct = state.taskInfoList.find(taskInfo => taskInfo.taskNameId === taskNameId);
        // 是否是主动任务(非手动执行任务的任务都是主动执行任务)
        const isAutoTask = autoExecConfig?.execTaskType !== ExecTaskType.manual;
        return hasExecConfig && isInUserAct && isAutoTask;
      });
      if (!taskConfig) return;
      // 查到主动任务信息进行返回
      const { taskNameId, autoExecConfig } = taskConfig;
      const { maxTimesPerStage, finishCount } = state.taskInfoList.find(item => item.taskNameId === taskNameId)!;
      const isDone = finishCount >= maxTimesPerStage;
      return {
        taskNameId,
        isDone,
        autoExecConfig,
      };
    },
    /** 任务组信息 */
    getTaskGroupInfo() {
      return (taskGroupType: TaskType) => this.taskGroupList.find(item => item.taskGroupType === taskGroupType)!;
    },

    /** 获取单条信息 */
    getTaskItem() {
      return (taskInfoItem: TaskInfoItem): TaskItem => {
        const {
          taskNameId,
          maxTimesPerStage,
          finishCount,
          taskSendCnt,
          taskTotalCnt,
          taskInfoDetail,
        } = taskInfoItem;
        const { taskItemConfig } = useConfigStore().config.taskConfig;
        /** 配置中的任务数据 */
        const taskConfigInfo = taskItemConfig.find(item => item.taskNameId === taskNameId)!;
        const { taskType, taskName, taskRule, maxTimePerDay,
          taskStartTime, taskEndTime, stampCntPerTime, sort, taskSendLimit, taskLogo } = taskConfigInfo;
        return {
          id: taskNameId,
          order: sort,
          type: taskType,
          rule: taskRule,
          name: taskName,
          maxTimesPerStage,
          maxTimesPerDay: maxTimePerDay,
          prizeCountPerTime: stampCntPerTime,
          finishCount,
          status: formateTaskStatus({ ...taskConfigInfo, ...taskInfoItem }),
          startTime: taskStartTime as Date,
          endTime: taskEndTime as Date,
          jumpInfo: formatTaskJumpInfo(taskConfigInfo),
          execConf: taskConfigInfo.autoExecConfig,
          taskSendLimit,
          taskSendCnt,
          taskTotalCnt,
          taskInfoDetail,
          taskLogo,
        };
      };
    },

    /** 任务信息 */
    getTaskInfo(state) {
      return (taskNameId: string): TaskItem => {
        /** 后端返回的任务数据 */
        const taskInfoItem = state.taskInfoList.find(item => item.taskNameId === taskNameId)!;
        return this.getTaskItem(taskInfoItem);
      };
    },

    /**
     * 全部任务
     * @param state
     * @returns
     */
    allTasks(state): Array<TaskInfoItem> {
      return state.taskInfoList;
    },

    /**
     * 摘要任务列表
     * https://tapd.woa.com/10145891/prong/stories/view/1010145891883406775
     */
    summaryTasks() {
      const tasks = this.allTasks as Array<TaskInfoItem>;

      // 得到“未全部完成、未结束、印花未派完”的全部任务
      const taskItemsWithPriorityGiven: TaskItem[] = tasks.map((item) => {
        const taskItem = this.getTaskItem(item);

        const { status } = taskItem;
        // 要求未结束、印花未派完
        if (status !== TaskStatus.inProgress) {
          return taskItem;
        }

        const { finishCount, maxTimesPerStage } = item;
        // 要求未全部完成
        if (finishCount >= maxTimesPerStage) {
          return taskItem;
        }

        return {
          ...taskItem,
          // 通过扩大其职来增强它的优先级，但同时保持优先级内的order排序
          order: 100000 + taskItem.order,
        };
      });

      // 根据货源系统配置优先级排序
      const sortedTaskItems = taskItemsWithPriorityGiven.sort((a, b) => {
        if (a.order > b.order) {
          return -1;
        }
        if (a.order < b.order) {
          return 1;
        }
        return 0;
      });

      return sortedTaskItems;
    },

    /**
     * 基础任务列表
     * @returns
     */
    basicTasks() {
      const basicTaskGroup = this.getTaskGroupInfo('basic') as ReturnType<typeof this.taskGroupList>[number];
      const { taskInfoList } = basicTaskGroup;
      // @ts-ignore
      const basicTasks = taskInfoList.map(item => this.getTaskItem(item));
      return basicTasks as unknown as TaskItem[];
    },

    /**
     * 摘要首屏任务列表，2个
     * https://tapd.woa.com/10145891/prong/stories/view/1010145891883406775
     */
    summaryMainTasks(): TaskItem[] {
      const sortedTaskItems = this.summaryTasks as TaskItem[];
      return sortedTaskItems.slice(0, 2);
    },

    /**
     * 摘要轮播任务列表，1-3个
     * https://tapd.woa.com/10145891/prong/stories/view/1010145891883406775
     */
    summarySecondaryTasks(): TaskItem[] {
      const sortedTaskItems = this.summaryTasks as TaskItem[];
      const secondaryItems = sortedTaskItems.slice(2, 5);

      if (secondaryItems.length) {
        return secondaryItems;
      }

      // 如果未全部完成、未结束、印花未派完的任务<=2，默认推荐/滚动基础任务内容
      return this.basicTasks as unknown as TaskItem[];
    },
  },
  actions: {
    /**
     * 初始化任务信息
     */
    async initTaskInfo() {
      const { config } = useConfigStore();
      const { taskGroupConfig, taskItemConfig } = config.taskConfig;
      /** 查询所有任务数据 */
      const taskShowRes = await taskShow({
        actIdList: taskGroupConfig.map(item => item.taskId),
      });
      console.log('查询到后端返回任务数据：', taskShowRes.actTaskInfoList, '配置任务数据：', taskItemConfig);
      /** 查询任务黑白名单 */
      const checkActList = getCheckActList(taskGroupConfig);
      const checkUserActRes = await checkUserAct({ checkActList });
      console.log('查询到后端返回黑白名单数据：', checkUserActRes);
      /** 过滤出用户可参与的任务记录列表 */
      const validTaskList = getValidTaskList({
        taskGroupConfig,
        taskItemConfig,
        taskShowRes,
        checkUserActRes,
      });
      // 校验任务状态并上报
      validTaskList.forEach(taskInfo => checkTaskAndReport(taskInfo));
      /** format成taskInfoList */
      this.taskInfoList = getTaskInfoList(validTaskList);
      checkAndReportTaskCnt({ validTaskList, taskInfoList: this.taskInfoList });
    },
    /**
     * 执行用户任务列表中的主动任务
     */
    async execAutoTask() {
      if (!this.autoExecTaskInfo) return false;
      const { isDone, autoExecConfig } = this.autoExecTaskInfo;
      if (isDone || !autoExecConfig) return false;
      // 执行任务
      return execTask(autoExecConfig);
    },
  },
});

/**
 * 针对任务数据有异常，如展示的用户完成量>最大完成量，则new CustomException
 */
function checkAndReportTaskCnt({ validTaskList, taskInfoList }) {
  const userTaskLog = `用户任务数据：${validTaskList.map(item => `${item.taskName}:${item.totalCnt}`).join('|')}`;
  const taskInfoLog = `任务数据合并：${taskInfoList.map(item => `${item.taskNameId}:${item.finishCount}/${item.maxTimesPerStage}`)}`;
  console.log(userTaskLog);
  console.log(taskInfoLog);
  elk({
    key: 'checkAndReportTaskCnt',
    msg: `${userTaskLog};${taskInfoLog}`,
  });
  taskInfoList.forEach((item) => {
    const { finishCount, maxTimesPerStage } = item;
    if (finishCount > maxTimesPerStage) {
      new CustomException(item, 'invalidTaskCnt', '单个任务已完成数大于限额');
    }
  });
}

/**
 * 过滤当前用户命中的任务列表
 * 返回的是taskShowRes.actTaskInfoList过滤后的数据
 * 根据黑名单checkUserActRes过滤一轮 & 根据前端配置taskItemConfig过滤一轮
 */
function getValidTaskList({
  taskGroupConfig,
  taskItemConfig,
  taskShowRes,
  checkUserActRes,
}: {
  taskGroupConfig: TaskGroupConfig[],
  taskItemConfig: TaskItemConfig[],
  taskShowRes: TaskShowRes,
  checkUserActRes?: CheckUserActRes,
}) {
  // 过滤黑白名单任务组
  const validTaskGroupInfo = taskShowRes.actTaskInfoList
    .filter(item => {
      if (!checkUserActRes) return true;
      const { actId } = item;
      const blackListConfig = taskGroupConfig.find(item => item.taskId === actId)?.blackListConfig;
      if (!blackListConfig) return true;
      const { blackListActId } = blackListConfig;
      const checkUserActInfo = checkUserActRes.userActInfoList
        .find(item => item.actListid === blackListActId);
      if (checkUserActInfo && !checkUserActInfo.isExpired) {
        return checkUserActInfo.isValid;
      }
      return false;
    });
  const allTaskInfo = flatten(validTaskGroupInfo
    .map(item => ([] as any[]).concat(item.basicTaskList).concat(item.specTaskList)));
  // 过滤配置和后端返回taskNameId不一致的
  const validTaskInfo = allTaskInfo.filter(item => {
    const { taskName } = item;
    return taskItemConfig.find(item => item.taskNameId === taskName);
  });

  return validTaskInfo;
}

/**
 * 对后端的任务列表数据进行格式化
 * 获取任务名称、周期最大完成次数、已完成数目
 */
function getTaskInfoList(validTaskList: TaskInfo[]) {
  const taskInfoList: TaskStore['taskInfoList'] = [];
  /** 获取所有任务NameId列表，去重，任务完成多次时会有同NameId的记录 */
  const validTaskNameIdList = Array.from(new Set(validTaskList.map(item => item.taskName)));
  validTaskNameIdList.forEach(taskNameId => {
    /** 当前taskNameId的任务 */
    const currentTaskList = validTaskList.filter(item => item.taskName === taskNameId);
    /** 当前任务的已派印花数量、任务总限额，取第一条（同个taskName任务每条记录taskSendCnt一样） */
    const { taskSendCnt, taskTotalCnt } = currentTaskList[0];
    /** * 当前任务的最大完成次数，取第一条的maxTimes(每条记录maxTimes都一样) */
    const taskMaxTimes = currentTaskList[0].maxTimes;
    /** 当前taskNameId本周期的任务记录 */
    const currentStageTaskList = currentTaskList.filter(item => {
      const { completeTime } = item;
      if (!completeTime) return true; // 没有完成时间的是还没有完成的
      const { firstDate, lastDate } = getMonthPeriod();
      const completeDate = dayjs(completeTime);
      // 有完成时间的只保留当月记录
      return completeDate.isAfter(firstDate) && completeDate.isBefore(lastDate);
    });
    /** 当前taskNameId上周期的任务记录 */
    const pastStageTaskList = currentTaskList.filter(item => !currentStageTaskList.includes(item));
    // 上个周期完成的记录数
    const pastStageDoneCout = pastStageTaskList.length;
    /** 任务最大完成次数，减去上周期已完成的，即为本周期可完成的最大次数 */
    const maxTimesCurStage = taskMaxTimes - pastStageDoneCout;
    // 本月该任务没有可完成的记录
    if (maxTimesCurStage === 0) return;
    /** 任务已完成次数，即本周期的任务记录中，已完成的任务记录数 */
    const finishCount = currentStageTaskList.filter(item => {
      const { totalCnt, requiredCount } = item;
      return isTaskCompleted({ totalCnt, requiredCount });
    }).length;
    taskInfoList.push({
      taskNameId,
      maxTimesPerStage: maxTimesCurStage,
      finishCount,
      taskSendCnt,
      taskTotalCnt,
      taskInfoDetail: currentStageTaskList,
    });
  });
  return taskInfoList;
}

/**
 * 从配置中获取查询任务黑白名单的数据
 */
function getCheckActList(taskGroupConfig: TaskGroupConfig[]) {
  return taskGroupConfig
    .filter(item => item.blackListConfig?.blackListActId)
    .map(item => ({
      actListid: item.blackListConfig!.blackListActId,
      checkType: item.blackListConfig!.blackListType,
    }));
}
/**
 * 格式化跳转配置
 * @param configJumpInfo 跳转配置
 */
function formatTaskJumpInfo(configJumpInfo: Pick<TaskItemConfig, 'btnLink' | 'btnMpConfig'>): JumpInfo | undefined {
  const { btnLink, btnMpConfig } = configJumpInfo;
  if (btnLink) {
    return {
      mode: 'h5',
      url: btnLink,
    };
  }
  if (btnMpConfig) {
    return {
      mode: 'mp',
      path: btnMpConfig.path,
      username: btnMpConfig.username,
    };
  }
  return undefined;
}

/**
 * 格式化任务状态：未开始，进行中，已结束，已派完
 * @param taskInfo 任务信息
 */
function formateTaskStatus(taskInfo:
Pick<TaskItemConfig, 'taskStartTime' | 'taskEndTime' | 'taskSendLimit'>
& Pick<TaskInfoItem, 'taskSendCnt'>) {
  const { taskStartTime, taskEndTime, taskSendCnt, taskSendLimit } = taskInfo;
  if (taskSendLimit && taskSendCnt && taskSendCnt >= taskSendLimit) {
    return TaskStatus.soldOut;
  }
  if (getServerTime() < taskStartTime) return TaskStatus.notStart;
  if (getServerTime() > taskEndTime) return TaskStatus.outdate;
  return TaskStatus.inProgress;
}

/**
 * 本周期内任务是否能继续参加(未结束 && 未完成)
 * @param taskItem 任务信息
 */
function isTaskCanDo(taskItem: TaskItem) {
  const { finishCount, maxTimesPerStage, endTime } = taskItem;
  const isNotDone = finishCount < maxTimesPerStage;
  const isNotEnd = getServerTime() <= endTime;
  return isNotDone && isNotEnd;
}

/**
 * 获取限时任务的权重
 * 对于限时任务，权重为：${时间权重}${进度权重}${配置权重}
 * 例如一个配置权重为90、进行中、已完成的限时任务，权重为：31090
 */
function getLimitTaskOrder(taskItem: TaskItem) {
  const { status, finishCount, maxTimesPerStage, order } = taskItem;
  const isFinish = finishCount >= maxTimesPerStage;
  // 时间权重
  const statusOrderMap = {
    [TaskStatus.inProgress]: '4', // 进行中
    [TaskStatus.notStart]: '3', // 未开始
    [TaskStatus.soldOut]: '2', // 已派完
    [TaskStatus.outdate]: '1', // 已结束
  };
  // 进度权重
  const progressOrderMap = {
    false: '2', // 未完成
    true: '1', // 已完成
  };
  // 状态权重 = 时间权重*10 + 进度权重，例如进行中的已完成的状态权重为31
  const specialOrderStr = `${statusOrderMap[status]}${progressOrderMap[String(isFinish)]}`;
  // 配置权重，000-999
  const configOrder = fillNum(order, 3).slice(0, 3);
  return +`${specialOrderStr}${configOrder}`;
}

/**
  * 获取任务的权重
  * 对于限时任务：调用getLimitTaskOrder;
  * 对于其他任务，权重为：${配置权重}
  * 例如一个配置权重为80的专属｜基础任务，权重为：80
  */
export function getTaskOrder(taskItem: TaskItem) {
  const { type, order } = taskItem;
  if (type === 'limit') {
    return getLimitTaskOrder(taskItem);
  }
  return order;
}

/**
 * 校验任务状态，有问题则上报
 */
export function checkTaskAndReport(taskInfo: TaskInfo) {
  const { status, requiredCount, totalCnt } = taskInfo;
  const isDone = isTaskCompleted({ totalCnt, requiredCount });
  const isValidStatus = status === 4;
  // 已完成任务但未派发
  if (isDone && !isValidStatus) {
    elk({
      key: 'StampTaskStatusValid',
      msg: JSON.stringify(taskInfo),
    });
  }
}

/**
 * 执行任务
 */
export async function execTask(autoExecConfig: ExecConf): Promise<Boolean> {
  // 執行
  const { autoExecTaskId, autoExecQualId } = autoExecConfig;
  const { execState } = await execUseract({
    actListid: autoExecTaskId,
    prizeNo: autoExecQualId,
  });
  return execState === ExecActState.success;
}

/** 判断任务是否已完成 */
export const isTaskCompleted = ({ requiredCount, totalCnt }: {requiredCount: number, totalCnt: number}) => {
  const isCompleted = totalCnt >= requiredCount;
  return isCompleted;
};
